import React, { useState, useEffect, useCallback, useRef } from 'react';
import lodash from 'lodash';
import { useHistory } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';

import {
  TextField,
  CircularProgress,
  Autocomplete,
  IconButton,
  Divider,
  Box,
  Chip,
} from '@mui/material';
import SearchTwoToneIcon from '@mui/icons-material/SearchTwoTone';
import { makeStyles, Theme, createStyles } from '@material-ui/core/styles';
import { Typography } from '@material-ui/core';
import Tooltip from '@mui/material/Tooltip';
import PinDropIcon from '@mui/icons-material/PinDrop';
import HistoryIcon from '@mui/icons-material/History';
import ClearIcon from '@mui/icons-material/Clear';
import DrawIcon from '@mui/icons-material/Draw';

import { callApi } from 'src/core/callApi';
import {
  COLOR_PRIMARY_LIGHT,
  COLOR_PRIMARY,
  FREE_MARKET_SEARCH_LIMIT,
  MARKET_SEARCH_HISTORY,
} from 'src/utils/const';
import {
  returnLoadingText,
  makeLowerCase,
  geocodeAddress,
  isMarketSearchAllowed,
} from 'src/utils/functions';
import { CachedSearchedMarketsType, DashboardState, SearchObject } from 'src/interfaces/idashboard';
import { SearchBarPropTypes } from 'src/interfaces/iproptypes';
import {
  fetchCachedMarketData,
  getDistrictInfo,
  setSearchLimitAlertBox,
  getMarketGrade,
  getSearchLoadStart,
} from 'src/store/Dashboard/actions';
import { VerifiedTag } from '../Tags';
import MarketSearch from 'src/core/MarketSearch';
import {
  COLOR_BACKGROUND_2,
  COLOR_TEXT_4,
  COLOR_TEXT_3,
  DEFAULT_TEXT_COLOR,
  COLOR_GREY_600,
  COLOR_PRIMARY_2,
  COLOR_PRIMARY_1,
} from 'src/utils/colors';
import { logAmplitudeEvent } from 'src/utils/analytics';
import { gridColumnsSelector } from '@mui/x-data-grid';
import { timeZoneCCMap } from 'src/App/assets/timeZoneCCMap';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    options: {
      '&:last-child': {
        paddingBottom: '40px',
      },
      '&:hover': {
        backgroundColor: COLOR_PRIMARY_LIGHT,
        fontColor: '#abcdef',
        textColor: '#abcdef',
      },
    },
  })
);

const searchBarInputStyles = {
  main: {
    inputBox: {
      color: COLOR_TEXT_4,
      backgroundColor: COLOR_BACKGROUND_2,
      borderRadius: 200,
      '& .MuiOutlinedInput-input': {
        margin: '0px 5px',
        marginRight: '8px',
      },
      '& .MuiOutlinedInput-notchedOutline': {
        border: 'none !important',
        boxShadow: '0px 2px 12px -6px rgba(0, 0, 0, 0.4)',
      },
      input: {
        '&::placeholder': {
          color: COLOR_GREY_600,
          fontWeight: 500,
          opacity: 1,
        },
      },
    },
  },
  compare: {
    inputBox: {
      color: COLOR_TEXT_4,
      backgroundColor: '#e5f4f4',
      borderRadius: 4,
      padding: '4px !important',
      '& .MuiOutlinedInput-notchedOutline': {
        border: 'none !important',
      },
    },
  },
};

const getSearchBarStyle = (searchbarStyle: 'main' | 'compare' | undefined, elementType: string) => {
  if (!searchbarStyle) {
    searchbarStyle = 'main';
  }

  return searchBarInputStyles[searchbarStyle][elementType];
};

const reArrangeOptions = (
  options: SearchObject[],
  inputRef: React.MutableRefObject<HTMLInputElement | undefined>
) => {
  let reArrangedOptions = [] as SearchObject[];
  const fastSearchOptions = options.filter(option => option.isFastSearchResult);
  const savedRegions = options.filter(option => option.isSavedRegion);
  const lambdaOptions = options.filter(option => option.isSearchLambdaResult);
  const isDefaultOption = options.filter(option => option.isDefaultOption);
  reArrangedOptions =
    inputRef?.current?.value == '' ? [...savedRegions] : [...fastSearchOptions, ...lambdaOptions];

  return [...reArrangedOptions, ...isDefaultOption];
};

const SEARCH_MARKET_PLACEHOLDER = 'Search for a market ...';
const TAILORED_REGION_PLACEHOLDER = 'Draw your Custom Market';
const SEARCH_COMPARE_MARKET_PLACEHOLDER = 'Add Market ...';

const MarketSearchBar = ({
  search,
  showAlert,
  notShowAlert,
  setdashboardState,
  user,
  district,
  isUserVerified,
  isOnboardingOn,
  isMobileScreen,
  cachedSearchMarkets,
  closeMobileSearch,
  source,
  showBasicSearchbar,
  handleCustomOnClose,
  handleCustomOnSearchChange,
  searchbarStyle,
  searchbarType,
  cachedTop10MarketCountryWise,
  setOverviewDashboardLayoutView,
  dummyMarket,
  handleOnSampleClick,
  setIsTailoredRegionOpen,
}: SearchBarPropTypes) => {
  const history = useHistory();

  const classes = useStyles();
  const dispatch = useDispatch();

  const [open, setOpen] = useState<boolean>(false);
  const [placeholderText, setPlaceholderText] = useState<string>(
    searchbarType === 'compare' || source === 'landing_page'
      ? SEARCH_COMPARE_MARKET_PLACEHOLDER
      : SEARCH_MARKET_PLACEHOLDER
  );
  const [options, setOptions] = useState<SearchObject[]>([] as SearchObject[]);

  const [searchBarState, setSearchBarState] = useState<string>('');
  const [loadingState, setLoadingState] = useState<boolean>(false);

  const [isSearchBarActive, setIsSearchBarActive] = useState<boolean>(false);

  const [marketSearch, setMarketSearch] = useState<MarketSearch | null>(null);
  const searchTooltipRef = useRef<any>();
  const inputRef = useRef<HTMLInputElement>();
  const [selectedOption, setSelectedOption] = useState(null);
  const [inputValue, setInputValue] = useState('');

  const loading = open && loadingState;
  const emptySearchBar = open && searchBarState.length === 0;

  const userPrefCountryCode = cachedSearchMarkets.userPrefCountryCode;

  useEffect(() => {
    setPlaceholderText(
      window.location.pathname.includes('tailored')
        ? TAILORED_REGION_PLACEHOLDER
        : searchbarType === 'compare' || source === 'landing_page'
        ? SEARCH_COMPARE_MARKET_PLACEHOLDER
        : SEARCH_MARKET_PLACEHOLDER
    );
  }, [search, district]);

  useEffect(() => {
    const initizeSearch = async () => {
      const marketSearch = new MarketSearch(cachedSearchMarkets);
      setMarketSearch(marketSearch);
    };

    cachedSearchMarkets && initizeSearch();
  }, [cachedSearchMarkets]);

  const getRedirectionLink = (
    prev: DashboardState,
    countryParam: string = '',
    cityParam: string = '',
    districtParam: string = '',
    coordinateParam: string = '',
    isGoogleAddress: boolean = false
  ) => {
    let paramStr = '';
    if (isGoogleAddress) {
      // paramStr = `${coordinateParam}/${districtParam}`;
      const [lat, lng] = coordinateParam
        .replace('(', '')
        .replace(')', '')
        .split(',');
      return `/tailored/t?lat=${lat}&lng=${lng}`;
    } else {
      paramStr = `${countryParam}/${cityParam}/${districtParam}`;
      return `/airbnb-data/${paramStr}`;
    }
  };

  const updateUserPrefCountryCode = (countryCode: string) => {
    localStorage.setItem('user-pref-country-code', countryCode);
  };

  const userTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  let loginCountryCode = timeZoneCCMap[userTimeZone] || 'US';

  const handleMarketSearchChange = values => {
    dispatch(getSearchLoadStart());
    setOverviewDashboardLayoutView('dashboard');
    let countryParam: string = values.country
      ? makeLowerCase(values.country).replace(/ /g, '-')
      : '0';
    let cityParam: string = values.city ? makeLowerCase(values.city) : '0';
    let districtParam: string = makeLowerCase(values.name);
    let countryCodeParam: string = values.countryCode ? values.countryCode : '';
    let marketID: string = values.id ? values.id : '';

    let redirectTo = `airbnb-data/${countryParam}/${cityParam}/${districtParam}`;
    redirectTo += marketID ? `?market_id=${marketID}` : '';
    window.history.replaceState({}, '', `${window.location.origin}/${redirectTo}`);
    dispatch(
      getDistrictInfo(
        loginCountryCode,
        districtParam,
        cityParam,
        countryParam,
        false,
        false,
        marketID
      )
    );
    dispatch(
      fetchCachedMarketData(
        marketID,
        districtParam,
        cityParam,
        countryCodeParam,
        countryParam,
        loginCountryCode
      )
    );
    dispatch(getMarketGrade(parseInt(marketID)));
  };

  const handleSearchChange = useCallback(
    async (_, values) => {
      setSelectedOption(values);
      updateUserPrefCountryCode(values?.countryCode);

      const search_attempt_history = localStorage.getItem('investment-search-attempts');
      const noOfUserSearch = search_attempt_history ? parseInt(search_attempt_history) : 0;

      if (!isMarketSearchAllowed(user, values.id, dummyMarket.id)) {
        handleOnSampleClick('expert');
        clearSearchBar();
        return;
      }

      if (!values) {
        return;
      }

      if (values.isDefaultOption) {
        history.push('/tailored/t');
        return;
      }

      if (values.isGoogleAPIResult) {
        const [lat, lng] = (await geocodeAddress(values.name)) as [number, number];
        const coordinate = `(${lat},${lng})`;

        setdashboardState(prev => {
          const redirectTo = getRedirectionLink(prev, '', '', values.name, coordinate, true);
          const searchAttemptHistory = localStorage.getItem(MARKET_SEARCH_HISTORY) || '{}';
          const userSearchHistory = JSON.parse(searchAttemptHistory);
          window.history.pushState({}, '', redirectTo);
          window.location.reload();
          return prev;
        });
        return;
      }

      if (values.redirectToTailoredRegion) {
        let tailoredAddress: string = `/tailored/t?lat=${values.lat}&lng=${values.lng}`;
        if (values.name) {
          tailoredAddress += '&name=' + values.name;
        }
        history.push(tailoredAddress);
        return;
      }

      handleMarketSearchChange(values);

      // re initialize the search bar state
      setSearchBarState('');
      setOptions([]);
      setLoadingState(false);
      setOpen(false);
      setSelectedOption(null);
      setInputValue('');
    },
    [isUserVerified]
  );

  const lastPromise = useRef();

  const formatSearchBarListingNames = (
    district: string,
    displayName: string | null,
    city: string,
    country: string,
    parentMarketName: string | null
  ): string => {
    if (!district) return '';

    let result = displayName || district;

    if (parentMarketName) {
      result += ' (' + parentMarketName + ', ' + country.replace('-', ' ') + ')';
      return result;
    }

    if (city && city != '0') {
      result += ', ' + city;
    }

    result += ` ( ${country.replace('-', ' ')} )`;
    return result;
  };

  const handleInputChangeFastSearch = useCallback(
    lodash.debounce(async values => {
      dispatch(setSearchLimitAlertBox(false));

      if (values.length > 0) {
        setSearchBarState(values);
        setLoadingState(true);

        try {
          let searchObjs: SearchObject[] = [];

          //===========================================
          // FAST SEARCH
          //

          const fastSearchResults: CachedSearchedMarketsType[] = marketSearch
            ? marketSearch.calculate(values)
            : [];

          fastSearchResults.forEach(market => {
            searchObjs.push({
              // searchName: `${market.district}, ${market.city} ( ${market.country} )`,
              searchName: formatSearchBarListingNames(
                market.district,
                market.displayName,
                market.city,
                market.country,
                market.parentMarketName
              ),
              id: market.id,
              name: market.district,
              city: market.city,
              country: market.country,
              priority: market.priority,
              isSearchLambdaResult: false,
              isVerified: market.isVerified,
              isFastSearchResult: true,
              countryCode: market.countryCode,
              matches: market.matches,
              parentMarketName: market.parentMarketName,
            });
          });

          searchObjs.push({
            searchName: values,
            name: 'TailoredRegion',
            isDefaultOption: true,
            isVerified: false,
            priority: 'T1',
            parentMarketName: null,
          });

          setOptions(searchObjs);
        } catch (err) {
          console.log('Error fast search: ', err);
        }
      } else {
        setLoadingState(false);
        const top10MarketsCountryWise = showTop10MarketsCountryWise();
        setOptions(prev => [...prev, ...top10MarketsCountryWise]);
      }
    }, 300),
    [marketSearch, inputRef.current]
  );

  const handleInputChangeCallAPI = useCallback(
    lodash.debounce(async (event, values, lastPromise) => {
      if (values.length > 0 && values.trim() !== '') {
        let api: string =
          'https://zbg6tnxjk1.execute-api.us-east-2.amazonaws.com/default/searchAutoComplete';

        try {
          let searchObjs: SearchObject[] = [];

          const currentPromise = callApi(
            api,
            {
              queryParams: {
                prefix: values,
              },
            },
            true,
            false
          ).then(res => {
            return res;
          });
          lastPromise.current = currentPromise;

          currentPromise.then(
            response => {
              if (
                currentPromise === lastPromise.current &&
                response.message.constructor === Array
              ) {
                setLoadingState(false);
                if (response && response.message) {
                  if (response.message.length === 0) {
                    showAlert();
                  } else {
                    notShowAlert();
                  }

                  if (response.redirectToTailoredRegion) {
                    const { lat, lng } = response.coordinates;
                    searchObjs.push({
                      searchName: values + ' (select area from map)',
                      name: values,
                      lat,
                      lng,
                      redirectToTailoredRegion: true,
                      isVerified: false,
                      priority: 'T1',
                      parentMarketName: null,
                    });
                  }

                  response.message.forEach(function(item, index) {
                    let districtName: string = item[0];
                    let cityName: string = item[5];
                    let countryName: string = item[6];
                    let priority: 'T1' | 'T2' | 'T3' | 'T4' = item[7];

                    searchObjs.push({
                      searchName: formatSearchBarListingNames(
                        districtName,
                        null,
                        cityName,
                        countryName,
                        null
                      ),
                      name: item[0],
                      lat: item[2],
                      lng: item[1],
                      polygon: '',
                      multiPolygon: '',
                      city: item[5],
                      country: item[6],
                      countryCode: item[8],
                      bounds: item[9],
                      isSearchLambdaResult: true,
                      isVerified: item[10],
                      priority: priority,
                      parentMarketName: null,
                    });
                  });
                }

                // setOptions([]); // to reinitialize the options
                if (inputRef.current?.value !== '' && inputRef.current?.value.trim() !== '') {
                  setOptions(prev => [...prev, ...searchObjs]);
                }
              }
            },
            e => {
              if (currentPromise === lastPromise.current) {
                console.warn('fetch failure', e);
              }
            }
          );
        } catch (error) {
          console.error((error as any)?.message);
        }
      }
    }, 300),
    [marketSearch, inputRef.current]
  );

  const logSearchAttempt = useCallback(
    lodash.debounce(value => {
      if (value.trim().length === 0) return; // Ignore empty or whitespace-only values
      logAmplitudeEvent('market_search_attempted', { source, search_term: value });
    }, 300),
    []
  );

  useEffect(() => {
    let active = true;

    if (!loading) {
      return undefined;
    }

    return () => {
      active = false;
    };
  }, [loading]);

  useEffect(() => {
    if (!open) {
      setOptions([]);
    }
  }, [open]);

  const restorePlaceholderText = () =>
    setPlaceholderText(
      window.location.pathname.includes('tailored')
        ? TAILORED_REGION_PLACEHOLDER
        : SEARCH_MARKET_PLACEHOLDER
    );

  const removePlaceholderText = () => setPlaceholderText('');

  const clearSearchBar = () => {
    setSelectedOption(null);
    setInputValue('');
    setTimeout(() => {
      if (inputRef.current) {
        placeholderText == '' && inputRef.current.blur();
        inputRef.current.value = '';
      }
    }, 10);
    setSearchBarState('');
    setOptions([]);
    setOpen(false);
    closeMobileSearch && closeMobileSearch();
  };

  const handleCustomOnCloseWrapper = (event, value) => {
    handleCustomOnSearchChange(event, value);

    setSelectedOption(null);
    setInputValue('');

    if (inputRef.current) {
      inputRef.current.blur();
    }
  };

  const StartAdorement = () => {
    return <SearchTwoToneIcon sx={{ color: COLOR_PRIMARY_2, paddingLeft: 1, marginRight: 0 }} />;
  };

  const EndAdorement = () => {
    return (
      <>
        {/* loading animation */}
        {!emptySearchBar && loading ? (
          <CircularProgress sx={{ color: COLOR_PRIMARY }} size={20} />
        ) : null}
        {/* clear button */}
        {(inputRef.current?.value || isSearchBarActive) && (
          <IconButton sx={{ color: DEFAULT_TEXT_COLOR, paddingRight: 1 }} onClick={clearSearchBar}>
            <ClearIcon />
          </IconButton>
        )}
        {!isSearchBarActive && !showBasicSearchbar && !isMobileScreen && (
          <>
            {/* divider  */}
            <Divider
              orientation="vertical"
              flexItem
              sx={{ margin: '2px 10px', borderRightWidth: 3 }}
            />
          </>
        )}
        {/* custom market */}
        {!isSearchBarActive && !showBasicSearchbar && (
          <Tooltip title="Draw Custom Market">
            <IconButton
              sx={{ color: COLOR_TEXT_4, paddingRight: 1 }}
              onClick={() => {
                localStorage.setItem('market-search-source', source);
                setIsTailoredRegionOpen(true);
                return;
              }}
            >
              <DrawIcon />
            </IconButton>
          </Tooltip>
        )}
      </>
    );
  };

  const showTop10MarketsCountryWise = () => {
    let tempSearchObjs: SearchObject[] = [];
    try {
      const targetCountry =
        userPrefCountryCode &&
        cachedTop10MarketCountryWise &&
        cachedTop10MarketCountryWise[userPrefCountryCode]
          ? userPrefCountryCode
          : 'world';

      if (!cachedTop10MarketCountryWise) return tempSearchObjs;

      [...cachedTop10MarketCountryWise[targetCountry]].forEach(market => {
        tempSearchObjs.push({
          searchName: market.display_name
            ? market.display_name
            : market.name
            ? market.name
            : market.district,
          id: market.id,
          name: market.district,
          city: market.city,
          country: market.country,
          countryCode: market.country_code,
          isSavedRegion: true,
          isVerified: true,
          priority: 'T1',
          parentMarketName: null,
        });
      });

      tempSearchObjs.push({
        searchName: '',
        name: 'TailoredRegion',
        isDefaultOption: true,
        isVerified: false,
        priority: 'T1',
        parentMarketName: null,
      });

      return tempSearchObjs;
    } catch (err) {
      console.error('Error while setting top 10 markets in search: ', err);
      return tempSearchObjs;
    }
  };

  return (
    <Box sx={{ width: '100%' }}>
      <Autocomplete
        ref={searchTooltipRef}
        id="autocomplete"
        open={open}
        classes={{
          option: classes.options,
        }}
        sx={{
          '& .MuiInputBase-root': {
            border:
              isOnboardingOn && !showBasicSearchbar
                ? `2px solid ${COLOR_PRIMARY_1} !important`
                : 'none',
          },
        }}
        value={selectedOption}
        inputValue={inputValue}
        onOpen={() => {
          setOpen(true);
          setOptions(e => showTop10MarketsCountryWise());
        }}
        onClose={() => {
          setOpen(false);
          handleCustomOnClose && handleCustomOnClose();
        }}
        options={reArrangeOptions(options, inputRef)}
        filterOptions={(options, params) => {
          return options;
        }}
        loading={loading}
        onInputChange={async (event, value) => {
          setInputValue(value);
          setOptions([]);
          await handleInputChangeFastSearch(value);
          await handleInputChangeCallAPI(event, value, lastPromise);
          logSearchAttempt(value);
        }} // this one being called whenever user inputs a letter
        onChange={handleCustomOnSearchChange ? handleCustomOnCloseWrapper : handleSearchChange} // this one being called after user click the value in the dropdown
        noOptionsText={'No search result'}
        loadingText={returnLoadingText(emptySearchBar)}
        ListboxProps={{ style: isMobileScreen ? { maxHeight: '200px' } : {} }}
        disableCloseOnSelect={true}
        getOptionLabel={option => {
          return option.searchName;
        }}
        renderOption={(props, option) =>
          option.isDefaultOption && !showBasicSearchbar ? (
            <li {...props}>
              <Typography>
                <IconButton
                  style={{
                    paddingTop: 3,
                    paddingBottom: 3,
                    paddingLeft: 0,
                  }}
                >
                  <PinDropIcon
                    style={{
                      fontSize: 24,
                      display: 'inline-block',
                    }}
                  />
                </IconButton>
                Draw your search area
              </Typography>
            </li>
          ) : (
            <li {...props}>
              <Typography style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                <HighlightedText
                  text={capitalizeName(option.searchName)}
                  indices={option.matches?.[0]?.indices as [number, number][]}
                />
                {option.isVerified && <VerifiedTag size="small" />}
              </Typography>
            </li>
          )
        }
        renderInput={params => (
          <TextField
            {...params}
            inputRef={inputRef}
            placeholder={placeholderText}
            onFocus={() => {
              removePlaceholderText();
              setIsSearchBarActive(true);
            }}
            onBlur={() => {
              restorePlaceholderText();
              setIsSearchBarActive(false);
            }}
            InputLabelProps={{
              shrink: false,
              sx: {
                color: COLOR_TEXT_3,
              },
            }}
            variant="outlined"
            size={isOnboardingOn ? 'medium' : 'small'}
            InputProps={{
              ...params.InputProps,
              sx: getSearchBarStyle(searchbarStyle, 'inputBox'),
              startAdornment: <StartAdorement />,
              endAdornment: <EndAdorement />,
            }}
          />
        )}
      />
    </Box>
  );
};

const capitalizeName = (name: string): string => {
  let nameArray: string[] = name.split(' ');
  let capitalizedName: string = '';
  nameArray.forEach(item => {
    capitalizedName += item.charAt(0).toUpperCase() + item.slice(1) + ' ';
  });
  return capitalizedName;
};

export const HighlightedText = ({ text, indices }) => {
  if (!indices || indices.length === 0) {
    return <span>{text}</span>;
  }

  const parts: any[] = [];
  let lastIndex = 0;

  // Only one match is supported for now for accurate highlighting
  [indices[0]].forEach(([start, end], idx) => {
    // Add non-matched text before this match
    if (start > lastIndex) {
      parts.push(<span key={`text-${idx}-${lastIndex}`}>{text.substring(lastIndex, start)}</span>);
    }
    // Add matched text as bold
    parts.push(
      <span key={`mark-${idx}-${start}`} style={{ fontWeight: 'bold' }}>
        {text.substring(start, end + 1)}
      </span>
    );
    lastIndex = end + 1;
  });

  // Add the remaining text after the last match.
  if (lastIndex < text.length) {
    parts.push(<span key={`text-end`}>{text.substring(lastIndex)}</span>);
  }

  return <span>{parts}</span>;
};

export default React.memo(MarketSearchBar);
